import { defineStore } from 'pinia'
// @ts-ignore
import { ref, computed } from 'vue'
import type { User } from '@/types'
import archiWebAuthService from '@/services/archiweb.auth.service'
import { showApiWarningNotification } from '@/utils/globalNotifications'
import { formatDateTime } from '@/utils/formatters'
import router from '@/router'
import { useUiStore } from '@/stores'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const authChecked = ref(false)
  const accessToken = ref<string | null>(null)
  const refreshTokenValue = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Initialize tokens from storage on store creation
  async function attemptAutoLogin() {
    // Check if we're in an OAuth callback (has code and state parameters)
    const urlParams = new URLSearchParams(window.location.search)
    const hasCode = urlParams.has('code')
    const hasState = urlParams.has('state')
    
    if (hasCode && hasState) {
      // We're in a callback flow, don't start automatic login
      return
    }
    
    // Check if we're on a public route that doesn't require authentication
    const currentPath = window.location.pathname
    const publicRoutes = ['/', '/login', '/signup', '/reset-password', '/callback', '/payment/confirmation', '/privacy-policy', '/cookie-policy', '/terms-of-service', '/account-deletion']
    const isPublicRoute = publicRoutes.includes(currentPath)
    
    if (isPublicRoute) {
      // Don't attempt auto-login on public routes
      return
    }
    
    try {
      // For OAuth login, we don't need credentials - redirect to auth server
      await archiWebAuthService.login()
    } catch (error) {
      console.error('Login error:', error)
      const uiStore = useUiStore()
      uiStore.setError('Failed to start login process')
    }
  }

  // Attempt auto-login
  attemptAutoLogin()

  // Getters
  const getUser = computed(() => user.value)
  const getUserCurrency = computed(() => user.value?.settings?.currency || 'USD')
  const getAccessToken = computed(() => accessToken.value)
  const getRefreshToken = computed(() => refreshTokenValue.value)
  const isLoading = computed(() => loading.value)
  const getError = computed(() => error.value)

  // Actions
  async function setUser(newUser: User | null) {
    user.value = newUser
    isAuthenticated.value = !!newUser
    
    if (newUser && newUser.type === 'FIRM_OWNER') {
      const onboardingModule = await import('./onboardingStore')
      const onboarding = onboardingModule.useOnboardingStore()
      const status = await onboarding.refresh()
      if (!status.firmId) return
      // Import here to avoid circular dependency
      const firmStoreModule = await import('./firmStore')
      const firmStore = firmStoreModule.useFirmStore()
      await firmStore.loadUserFirm()
    }
  }

  function setAccessToken(token: string | null) {
    accessToken.value = token
    // Token persistence removed for security reasons
  }

  function setRefreshToken(token: string | null) {
    refreshTokenValue.value = token
    // Token persistence removed for security reasons
  }

  function setLoading(status: boolean) {
    loading.value = status
  }

  function setError(errorMessage: string | null) {
    error.value = errorMessage
  }

  function setAuthChecked(status: boolean) {
    authChecked.value = status
  }

  /**
   * Reload the connected user so dynamic permissions (for example those of a
   * newly created project) are immediately reflected in the UI.
   */
  async function refreshUserProfile(): Promise<boolean> {
    try {
      const refreshedUser = await archiWebAuthService.getUserProfile()

      if (!refreshedUser) return false

      user.value = refreshedUser
      isAuthenticated.value = true
      return true
    } catch (refreshError) {
      console.error('Failed to refresh user profile:', refreshError)
      return false
    }
  }

  function clearAuth() {
    user.value = null
    isAuthenticated.value = false
    accessToken.value = null
    refreshTokenValue.value = null
    
    // Token persistence removed for security reasons
    // No automatic clearing needed since persistence is disabled
    
    // Clear firm data when auth is cleared
    // Note: We don't import firmStore here to avoid circular dependency
    // The firm will be cleared when the user logs in again
  }

  async function logout() {
    try {
      // Call server logout (which also clears tokens from session storage)
      await archiWebAuthService.logout(false) // Don't auto-redirect, we'll handle it
    } catch (error) {
      console.error('❌ Error during server logout:', error)
    } finally {
      // Clear all authentication-related data locally
      clearAuth()
      
      // Clear other stores when logging out
      // Import here to avoid circular dependencies
      const projectStoreModule = await import('./projectStore')
      const firmStoreModule = await import('./firmStore')
      const uiStoreModule = await import('./uiStore')
      const onboardingStoreModule = await import('./onboardingStore')
      
      const projectStore = projectStoreModule.useProjectStore()
      const firmStore = firmStoreModule.useFirmStore()
      const uiStore = uiStoreModule.useUiStore()
      const onboardingStore = onboardingStoreModule.useOnboardingStore()
      
      // Clear all related data
      projectStore.clearCurrentProject()
      firmStore.clearSelectedFirm()
      uiStore.clearMessages()
      onboardingStore.clear()
      
      // Clear any additional session storage items
      try {
        sessionStorage.clear() // Clear all session storage for complete cleanup
      } catch (error) {
        console.warn('Failed to clear session storage:', error)
      }
      
      console.log('✅ Complete logout and cleanup finished')
      
      // Redirect to home page
      router.push('/')
    }
  }

  // Initialize authentication state from stored tokens
  async function initializeAuth() {
    const token = getAccessToken.value
    
    if (token) {
      setAccessToken(token)
      
      try {
        // Verify token and get user profile
        const userProfile = await archiWebAuthService.getUserProfile()
        
        if (userProfile) {
          await setUser(userProfile)
          // Show subscription grace notification on app init if needed
          try {
            const sub = (userProfile as any).subscriptionStatus
            if (sub && sub.inGrace) {
              const formatted = sub.graceUntil ? formatDateTime(sub.graceUntil) : sub.graceUntil
              // Simple translation helper: prefer user locale, then browser locale
              // Prefer global i18n translator if available
              const win: any = window as any
              const t = win && win.i18n && win.i18n.global && typeof win.i18n.global.t === 'function' ? win.i18n.global.t : null
              const message = formatted ? t('notifications.subscriptionGrace', { date: formatted }) : t('notifications.subscriptionGrace_noDate')
              showApiWarningNotification(message, { duration: 0 })
            }
          } catch (e) {
            console.warn('Failed to show subscription grace notification on init', e)
          }
          return true
        } else {
          // Token is invalid
          clearAuth()
          return false
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        clearAuth()
        return false
      }
    }
    
    return false
  }

  // Refresh token if needed
  async function refreshTokenIfNeeded() {
    if (!accessToken.value) {
      return false
    }

    try {
      const refreshed = await archiWebAuthService.refreshTokenIfNeeded()
      
      if (refreshed) {
        const currentToken = archiWebAuthService.getStoredAccessToken()
        if (currentToken && currentToken !== accessToken.value) {
          setAccessToken(currentToken)
        }
      }
      
      return true
    } catch (error) {
      console.error('Error refreshing token:', error)
      setError('Failed to refresh your session. Please log in again.')
      clearAuth()
      return false
    }
  }

  // Direct token refresh action
  async function performTokenRefresh() {
    if (!accessToken.value) {
      console.log('No access token available for refresh')
      return false
    }

    try {
      console.log('Calling auth service refreshToken...')
      const tokenResponse = await archiWebAuthService.refreshToken()
      
      if (tokenResponse && tokenResponse.access_token) {
        console.log('Token refresh successful, updating store')
        setAccessToken(tokenResponse.access_token)
        if (tokenResponse.refresh_token) {
          setRefreshToken(tokenResponse.refresh_token)
        }
        return true
      } else {
        console.log('Token refresh response missing access_token')
        return false
      }
    } catch (error) {
      console.error('Error refreshing token:', error)
      return false
    }
  }

  // Handle OAuth callback authentication
  async function authenticate({ code, state }: { code: string; state: string }) {
    try {
      setLoading(true)
      setError(null)
      
      const result = await archiWebAuthService.handleCallback(code, state)
      
      if (result.success && result.user) {
        setUser(result.user)
        setAccessToken(result.accessToken || null)
        if (result.refreshToken) {
          setRefreshToken(result.refreshToken)
        }
        isAuthenticated.value = true
        
        // Load firm data if user is a service provider
        if (result.user.type === 'FIRM_OWNER') {
          const firmStoreModule = await import('./firmStore')
          const firmStore = firmStoreModule.useFirmStore()
          await firmStore.loadUserFirm()
        }

        // If the user's subscription is in a grace period, show a warning with the expiry
        try {
          const sub = (result.user as any).subscriptionStatus
          if (sub && sub.inGrace) {
            const until = sub.graceUntil
            const formatted = until ? formatDateTime(until) : until
            const win: any = window as any
            const t = win && win.i18n && win.i18n.global && typeof win.i18n.global.t === 'function' ? win.i18n.global.t : null
            const message = formatted ? t('notifications.subscriptionGrace', { date: formatted }) : t('notifications.subscriptionGrace_noDate')
            showApiWarningNotification(message, { duration: 0 })
          }
        } catch (e) {
          // Non-critical: don't break authentication flow if notification fails
          console.warn('Failed to show subscription grace notification', e)
        }
        
        return true
      } else {
        setError(result.message || 'Authentication failed')
        return false
      }
    } catch (error) {
      console.error('Authentication error:', error)
      setError('An unexpected error occurred during authentication')
      return false
    } finally {
      setLoading(false)
    }
  }

  // Check authentication status on app initialization
  async function checkAuth() {
    const token = getAccessToken.value

    try {
      if (token) {
        setAccessToken(token)

        try {
          // Create a direct HTTP client to avoid circular dependency
          // during initialization where the service tries to access the store
          const axios = (await import('axios')).default;
          
          const response = await axios.get('/api/user/active', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.data) {
            await setUser(response.data)
            return true
          } else {
            // Token is invalid
            clearAuth()
            return false
          }
        } catch (error) {
          console.error('Error checking auth:', error)
          clearAuth()
          return false
        }
      }

      return false
    } finally {
      // Mark that we've completed the auth check, regardless of outcome
      setAuthChecked(true)
    }
  }

  return {
    // State
    user,
    isAuthenticated,
    authChecked,
    accessToken,
    refreshToken: refreshTokenValue,
    loading,
    error,
    
    // Getters
    getUser,
    getUserCurrency,
    getAccessToken,
    getRefreshToken,
    isLoading,
    getError,
    
    // Actions
    setUser,
    setAccessToken,
    setRefreshToken,
    setLoading,
    setError,
    setAuthChecked,
    refreshUserProfile,
    clearAuth,
    logout,
    initializeAuth,
    refreshTokenIfNeeded,
    performTokenRefresh,
    authenticate,
    checkAuth
  }
})
